import React, { useEffect, useContext, useState, useRef } from 'react';
import FadeIn from 'react-fade-in';
import { Observer } from 'mobx-react-lite';
import MaskedInput from 'react-text-mask'
import { GlobalHotKeys } from 'react-hotkeys';

import BodyEnd from '../../_shared/BodyEnd';
import ConfirmModal from '../../_shared/ConfirmModalComponent';
import HtmlEditor from '../../_shared/HtmlEditor';
import { quickDrawerFocus, renderQuickDrawerLoading } from '../../_shared/QuickDrawer';
import QuickDrawerHeader from '../../_shared/QuickDrawerHeader';

import PurchaseUpdateStore from '../../../../stores/PurchaseUpdateStore';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';

import * as ErrorMessages from '../../../../constants/errorMessages';
import * as MaskKeys from '../../../../constants/maskKeys';
import * as fn from '../../../../utilities/_functions';
import * as oh from '../../../../utilities/operationHelper';
import * as ph from '../../../../utilities/personHelper';
import * as ch from '../../../../utilities/customerHelper';
import * as wh from '../../../../utilities/workOrderHelper';
import * as ah from '../../../../utilities/addressHelper';
import * as bh from '../../../../utilities/badgeHelper';

import './PaymentRequest.scss';

function PaymentRequest(props) {
    const isMounted = useRef(true);
    const validateRef = useRef(null);
    const focusTimer = useRef(null);
    const confirmModalRef = useRef(null);
    const workOrderNoteRef = useRef(null);
    const workOrderNoteHtml = useRef(null);
    const purchase = useContext(PurchaseUpdateStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const [paymentRequestType, setPaymentRequestType] = useState('full');

    useEffect(() => {
        focusTimer.current = setTimeout(() => {
            quickDrawerFocus(props.drawer);
        }, 100);

        return () => {
            isMounted.current = false;
            purchase.clearPaymentRequest();
            if (focusTimer.current) { clearTimeout(focusTimer.current); focusTimer.current = null; }
        }
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (purchase.hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleAmountChange = event => {
        const amount = event.target.value ? fn.parseCurrency(event.target.value) : null;
        purchase.paymentRequest.amount = amount && amount > 0.0 ? amount : null;
        purchase.hasUnsavedChanges = true;
    }

    const handlePaymentRequestTypeChange = event => {
        const PaymentRequestType = event.target.value;

        setPaymentRequestType(PaymentRequestType);

        if (PaymentRequestType === 'full') {
            purchase.paymentRequest.amount = null;
        }

        purchase.hasUnsavedChanges = true;
    }

    const handleMessageChange = event => {
        const message = event.target.value;
        purchase.paymentRequest.message = message;
        purchase.hasUnsavedChanges = true;
    }

    const handleSendNotificationChange = event => {
        purchase.paymentRequest.sendNotification = !purchase.paymentRequest.sendNotification;
        purchase.hasUnsavedChanges = true;
    }

    const handleCollectShippingAddressChange = event => {
        purchase.paymentRequest.collectShippingAddress = !purchase.paymentRequest.collectShippingAddress;
        purchase.hasUnsavedChanges = true;
    }

    const handleUpdateWorkOrderChange = event => {
        purchase.paymentRequest.updateWorkOrder = !purchase.paymentRequest.updateWorkOrder;

        if (purchase.paymentRequest.updateWorkOrder === false) {
            purchase.paymentRequest.postCreationWorkOrderStatus = null;
            purchase.paymentRequest.postCreationWorkOrderNotePreview = null;
            purchase.paymentRequest.postCreationWorkOrderNoteHtml = null;
            purchase.paymentRequest.postPaymentWorkOrderStatus = null;
        }
 
        purchase.hasUnsavedChanges = true;
    }

    const handlePostCreationWorkOrderStatusChange = event => {
        const { value } = event.target;
        purchase.paymentRequest.postCreationWorkOrderStatus = value ? value : null;
        purchase.hasUnsavedChanges = true;
    }

    const handlePostPaymentWorkOrderStatusChange = event => {
        const { value } = event.target;
        purchase.paymentRequest.postPaymentWorkOrderStatus = value ? value : null;
        purchase.hasUnsavedChanges = true;
    }

    const handlePostPaymentWorkOrderNoteChange = (e) => {
        const html = e.value;
        const noteHtml = (html === '<p></p>') || (html === '<p><br></p>') ? '' : html;
        workOrderNoteHtml.current = noteHtml;
        purchase.hasUnsavedChanges = true;
    }

    const handleSubmit = event => {
        event.preventDefault();

        if (fn.validateForm(validateRef.current)) {
            confirmModalRef.current.show({
                icon: <i className='fal fa-money-bill-wave text-success mr-2'></i>,
                message: <>Continue to send <span className='fw-500'>&nbsp;Payment Request&nbsp;</span>to {purchase.data.customer ? ph.getPreferredFirstName(purchase.data.customer) : ''}? </>,
                option1ClassName: 'btn btn-success shadow-0 bootbox-accept',
                option1Text: 'Email Request',
                option2ClassName: 'btn btn-secondary shadow-0 bootbox-accept',
                option2Text: 'Copy Link',
                cancelText: 'Cancel',
                onOption1Click: e => handlePaymentRequestConfirm(e, 'email'),
                onOption2Click: e => handlePaymentRequestConfirm(e, 'copy'),
                onCancel: handlePaymentRequestCancel
            })
        }
    }

    const handlePaymentRequestConfirm = (event, action) => {
        if (!!purchase.paymentRequest.updateWorkOrder && workOrderNoteHtml.current) {
            const notePreview = fn.stripHtml(workOrderNoteHtml.current);

            purchase.paymentRequest.postCreationWorkOrderNotePreview = notePreview && notePreview.length > 200 ? notePreview.substring(0, 200) : notePreview;
            purchase.paymentRequest.postCreationWorkOrderNoteHtml = workOrderNoteHtml.current;
        }

        purchase.requestPayment(true)
            .then((data) => {
                if (isMounted.current) {
                    confirmModalRef.current.close();

                    if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                        props.onSuccess(event, { updated: true, action: action, data: { ...data, ...{ type: paymentRequestType } } });
                    }
                }
            })
    }

    const handlePaymentRequestCancel = (event) => {
        confirmModalRef.current.close();
    }

    return <>
        <Observer>{() =>
            <>
                {
                    (props.drawer === quickDrawer.drawerOpened) ?
                        <GlobalHotKeys
                            keyMap={{
                                close: ['esc'],
                            }}
                            handlers={{
                                close: event => {
                                    handleCancel(event)
                                },
                            }}
                            allowChanges={true}
                        /> : null
                }
            </>
        }</Observer>
        <form ref={validateRef} onSubmit={handleSubmit}>
            <Observer>{() =>
                <fieldset disabled={purchase.isSaving}>
                    <div className='new-payment-container quick-drawer'>
                        <QuickDrawerHeader
                            drawer={props.drawer}
                            icon={oh.getIcon('purchase', 'payment')}
                            action='Send'
                            category='Payment Request'
                            className='purchases'
                            onCancel={handleCancel}
                        />
                        <div className='quick-drawer-body'>
                            <Observer>{() =>
                                <>
                                    {
                                        purchase.data && purchase.isReady ?
                                            <FadeIn>
                                                <div className='body-content'>
                                                    <section>
                                                        <div className='row'>
                                                            <div className='col-6'>
                                                                <h3 className='purchase-title'>Invoice Detail</h3>
                                                                <p>Invoice # <strong>{purchase.data.invoiceNumber}</strong></p>
                                                            </div>
                                                            <div className='col-6'>
                                                                <div className='text-right'>
                                                                    {bh.renderPurchaseBalanceStatus(purchase.data, 'text-truncate text-truncate-md fs-sm p-2')}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </section>
                                                    <Observer>{() =>
                                                        <>
                                                            {
                                                                purchase.data.customer ?
                                                                    <section className='customer'>
                                                                        <div className='row'>
                                                                            <div className='col-12'>
                                                                                <label><small>Billing Information</small></label>
                                                                            </div>
                                                                        </div>
                                                                        <div className='row'>
                                                                            <div className='col-12'>
                                                                                <Observer>{() =>
                                                                                    <div
                                                                                        className='profile-wrapper'
                                                                                    >
                                                                                        <div className='profile'>
                                                                                            <span
                                                                                                className={`profile-image profile-initials rounded-circle d-flex text-white ${ch.getProfileBgColor(purchase.data.customer)} fw-500`}
                                                                                                title={purchase.data.customer.fullName}
                                                                                            >
                                                                                                {purchase.data.customer.initials}
                                                                                            </span>
                                                                                        </div>
                                                                                        <div className='description flex-1'>
                                                                                            <div className='name'>{ph.getFullName(purchase.data.customer, true)}
                                                                                                {
                                                                                                    purchase.data.customer.dateOfBirth || purchase.data.customer.sex || purchase.data.customer.gender || purchase.data.customer.pronoun ?
                                                                                                        <small className='text-nowrap ml-2'>({`${ph.getAge(purchase.data.customer.dateOfBirth)} ${ph.getSexGenderPronounDisplay(purchase.data.customer)}`.trim()})</small> : null
                                                                                                }
                                                                                            </div>
                                                                                            {
                                                                                                purchase.data.billingAddress ?
                                                                                                    <div className='info'>{ah.getAddressHtml(purchase.data.billingAddress)}</div> : null
                                                                                            }
                                                                                            {
                                                                                                purchase.data.customerEmail ?
                                                                                                    <div className='info'>
                                                                                                        <a
                                                                                                            href={`mailto:${purchase.data.customerEmail}`}
                                                                                                        >{purchase.data.customerEmail}
                                                                                                        </a>
                                                                                                    </div> : null
                                                                                            }
                                                                                            {
                                                                                                purchase.data.customerPhoneNumber ?
                                                                                                    <div className='info'>
                                                                                                        <a
                                                                                                            href={`tel:${purchase.data.customerPhoneNumber}`}
                                                                                                        >{purchase.data.customerPhoneNumber}
                                                                                                        </a>
                                                                                                    </div> : null
                                                                                            }
                                                                                        </div>
                                                                                    </div>
                                                                                }</Observer>
                                                                            </div>
                                                                        </div>
                                                                    </section> : null
                                                            }
                                                        </>
                                                    }</Observer>
                                                    <section>
                                                        <div className='row'>
                                                            <div className='col-4'>
                                                                <label><small>Invoice Total</small></label>
                                                                <Observer>{() =>
                                                                    <strong className='d-block fs-xl'>{fn.formatCurrency(purchase.data.total)}</strong>
                                                                }</Observer>
                                                            </div>
                                                            <div className='col-4'>
                                                                <label><small className={((purchase.paymentRequest.amount - purchase.data.remainingBalance) !== 0 ? 'text-danger' : '')}>Remaining</small></label>
                                                                <Observer>{() =>
                                                                    <strong className={'d-block fs-xl' + (purchase.paymentRequest.amount - purchase.data.remainingBalance !== 0 ? ' text-danger' : '')}>{fn.formatCurrency((purchase.data.remainingBalance - purchase.paymentRequest.amount))}</strong>
                                                                }</Observer>
                                                            </div>
                                                            {
                                                                !!purchase.data && !!purchase.data.customer && !!purchase.data.customer.storeCreditBalance ?
                                                                    <div className='col-4'>
                                                                        <label><small className={(purchase.data.customer.storeCreditBalance >= 0 ? 'text-info' : 'text-danger')}>Store Credit</small></label>
                                                                        <Observer>{() =>
                                                                            <strong className={'d-block fs-xl ' + (purchase.data.customer.storeCreditBalance >= 0 ? 'text-info' : 'text-danger')}>{fn.formatCurrency(purchase.data.customer.storeCreditBalance)}</strong>
                                                                        }</Observer>
                                                                    </div> : null
                                                            }
                                                        </div>
                                                    </section>
                                                    <section className={(!purchase.data || purchase.data.remainingBalance <= 0 ? 'd-none' : '')}>
                                                        <div className='row'>
                                                            <div className={paymentRequestType === 'full' ? 'col-12' : 'col-7'}>
                                                                <div className='form-group mb-4'>
                                                                    <label htmlFor='payment-request-type'><small>Request for</small></label>
                                                                    <select
                                                                        id='payment-request-type'
                                                                        className='custom-select form-control'
                                                                        value={paymentRequestType}
                                                                        onChange={handlePaymentRequestTypeChange}
                                                                    >
                                                                        <option value='full'>Remaining invoice balance</option>
                                                                        <option value='partial'>Specific amount</option>
                                                                    </select>
                                                                </div>
                                                            </div>
                                                            {
                                                                paymentRequestType === 'partial' ?
                                                                    <div className='col-5 validate validate-required'>
                                                                        <div className='form-group mb-4'>
                                                                            <label htmlFor='payment-request-amount' className='required'><small>Amount</small></label>
                                                                            <Observer>{() =>
                                                                                <MaskedInput
                                                                                    id='payment-request-amount'
                                                                                    type='text'
                                                                                    className='form-control currency-input validate-input'
                                                                                    mask={MaskKeys.CURRENCY_POSITIVE_MASK}
                                                                                    autoComplete='off'
                                                                                    value={purchase.paymentRequest.amount ? purchase.paymentRequest.amount : ''}
                                                                                    onChange={handleAmountChange}
                                                                                />
                                                                            }</Observer>
                                                                        </div>
                                                                    </div> : null
                                                            }
                                                        </div>
                                                        {
                                                            paymentRequestType === 'partial' ?
                                                                <div className='row'>
                                                                    <div className='col-12'>
                                                                        <div className='form-group mb-4 validate validate-required'>
                                                                            <label htmlFor='payment-request-message' className='required'><small>Reason <span className='text-normal'>(Shown in Email and Checkout Page)</span></small></label>
                                                                            <Observer>{() =>
                                                                                <textarea
                                                                                    id='payment-request-message'
                                                                                    className='form-control'
                                                                                    placeholder='E.g. Frame Deposit'
                                                                                    rows={5}
                                                                                    value={purchase.paymentRequest.message ? purchase.paymentRequest.message : ''}
                                                                                    onChange={handleMessageChange}
                                                                                />
                                                                            }</Observer>
                                                                        </div>
                                                                    </div>
                                                                </div> : null
                                                        }
                                                        <div className='row'>
                                                            <div className='col-12'>
                                                                <div className='form-group mb-1'>
                                                                    <label><small>Additional Actions</small></label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className='row'>
                                                            <div className='col-12'>
                                                                <div className='form-group mb-2'>
                                                                    <div className='custom-control custom-checkbox'>
                                                                        <input
                                                                            id='payment_request_notify_me'
                                                                            type='checkbox'
                                                                            name='payment_request_notify_me'
                                                                            className='custom-control-input'
                                                                            checked={!!purchase.paymentRequest.sendNotification}
                                                                            onChange={handleSendNotificationChange}
                                                                        />
                                                                        <label
                                                                            htmlFor='payment_request_notify_me'
                                                                            className='custom-control-label'
                                                                        >
                                                                            Notify me when payment is made
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className='row'>
                                                            <div className='col-12'>
                                                                <div className='form-group mb-2'>
                                                                    <div className='custom-control custom-checkbox'>
                                                                        <input
                                                                            id='payment_request_shipping_address'
                                                                            type='checkbox'
                                                                            name='payment_request_shipping_address'
                                                                            className='custom-control-input'
                                                                            checked={!!purchase.paymentRequest.collectShippingAddress}
                                                                            onChange={handleCollectShippingAddressChange}
                                                                        />
                                                                        <label
                                                                            htmlFor='payment_request_shipping_address'
                                                                            className='custom-control-label'
                                                                        >
                                                                            Ask for Shipping Information
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className='row'>
                                                            <div className='col-12'>
                                                                <div className='form-group mb-2'>
                                                                    <div 
                                                                        className={'custom-control custom-checkbox' + (!purchase.data.workOrders || !purchase.data.workOrders.some(o => wh.isActiveState(o)) ? ' cursor-not-allowed' : '')}
                                                                    >
                                                                        <input
                                                                            id='payment_request_update_work_order'
                                                                            type='checkbox'
                                                                            name='payment_request_update_work_order'
                                                                            className='custom-control-input'
                                                                            disabled={(!purchase.data.workOrders || !purchase.data.workOrders.some(o => wh.isActiveState(o)))}
                                                                            checked={!!purchase.paymentRequest.updateWorkOrder}
                                                                            onChange={handleUpdateWorkOrderChange}
                                                                        />
                                                                        <label
                                                                            htmlFor='payment_request_update_work_order'
                                                                            className={'custom-control-label' + (!purchase.data.workOrders || !purchase.data.workOrders.some(o => wh.isActiveState(o)) ? ' cursor-not-allowed' : '')}
                                                                            title={(!purchase.data.workOrders || !purchase.data.workOrders.some(o => wh.isActiveState(o)) ? 'No work order found.' : null)}
                                                                        >
                                                                            Update Work Order
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {
                                                            !!purchase.paymentRequest.updateWorkOrder && purchase.data.workOrders && purchase.data.workOrders.length > 0 ?
                                                                <>
                                                                    <div className='row'>
                                                                        <div className='col-12'>
                                                                            <div className='form-group mt-2 ml-6'>
                                                                                <label htmlFor='payment_request_update_work_order_request_status'><strong className='text-gray-700'>Once payment request is sent</strong></label>
                                                                                <select
                                                                                    id='payment_request_update_work_order_request_status'
                                                                                    className='custom-select form-control'
                                                                                    value={purchase.paymentRequest.postCreationWorkOrderStatus ? purchase.paymentRequest.postCreationWorkOrderStatus : ''}
                                                                                    onChange={handlePostCreationWorkOrderStatusChange}
                                                                                >
                                                                                    <option value=''>(Do not change status)</option>
                                                                                    <option value='ReadyToSubmit'>Ready to Submit</option>
                                                                                    <option value='Submitted'>Submitted</option>
                                                                                    <option value='ShippedToLab'>Shipped to Lab</option>
                                                                                    <option value='WaitingForFrame'>Waiting for Frame</option>
                                                                                    <option value='PartiallyReceived'>Partially Received</option>
                                                                                    <option value='ReturnedToLab'>Returned to Lab</option>
                                                                                    <option value='WaitingToEdge'>Waiting to Edge</option>
                                                                                    <option value='Ready'>Ready</option>
                                                                                    <option value='CustomerNotified'>Customer Notified</option>
                                                                                    <option value='Completed'>Completed</option>
                                                                                    <option value='OnHold'>On Hold</option>
                                                                                    <option value='Abandoned'>Abandoned</option>
                                                                                </select>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className='row'>
                                                                        <div className='col-12'>
                                                                            <div className='form-group mt-3 ml-6'>
                                                                                <label htmlFor='payment_request_update_work_order_paid_status'><strong className='text-gray-700'>Once payment is received</strong></label>
                                                                                <select
                                                                                    id='payment_request_update_work_order_paid_status'
                                                                                    className='custom-select form-control'
                                                                                    value={purchase.paymentRequest.postPaymentWorkOrderStatus ? purchase.paymentRequest.postPaymentWorkOrderStatus : ''}
                                                                                    onChange={handlePostPaymentWorkOrderStatusChange}
                                                                                >
                                                                                    <option value=''>(Do not change status)</option>
                                                                                    <option value='ReadyToSubmit'>Ready to Submit</option>
                                                                                    <option value='Submitted'>Submitted</option>
                                                                                    <option value='ShippedToLab'>Shipped to Lab</option>
                                                                                    <option value='WaitingForFrame'>Waiting for Frame</option>
                                                                                    <option value='PartiallyReceived'>Partially Received</option>
                                                                                    <option value='ReturnedToLab'>Returned to Lab</option>
                                                                                    <option value='WaitingToEdge'>Waiting to Edge</option>
                                                                                    <option value='Ready'>Ready</option>
                                                                                    <option value='CustomerNotified'>Customer Notified</option>
                                                                                    <option value='Completed'>Completed</option>
                                                                                    <option value='OnHold'>On Hold</option>
                                                                                    <option value='Abandoned'>Abandoned</option>
                                                                                </select>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className='row'>
                                                                        <div className='col-12'>
                                                                            <div className='form-group mt-3 ml-6'>
                                                                                <label htmlFor='payment-request-type'><strong className='text-gray-700'>Add note to work order</strong></label>
                                                                                <Observer>{() =>
                                                                                    <HtmlEditor
                                                                                        ref={workOrderNoteRef}
                                                                                        height='156px'
                                                                                        onValueChanged={handlePostPaymentWorkOrderNoteChange}
                                                                                    >
                                                                                    </HtmlEditor>
                                                                                }</Observer>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </> : null
                                                        }
                                                    </section>
                                                </div>
                                            </FadeIn> : renderQuickDrawerLoading()
                                    }
                                </>
                            }</Observer>
                        </div>
                        <div className='quick-drawer-action pl-3'>
                            <div className='row'>
                                <div className='col-12'>
                                    <div className='float-right'>
                                        <button
                                            type='button'
                                            className='btn btn-link btn-cancel mr-2'
                                            onClick={handleCancel}
                                        >Cancel</button>
                                        <button
                                            type='submit'
                                            className='btn btn-primary'
                                        >Continue</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </fieldset>
            }</Observer>
        </form>
        <BodyEnd>
            <ConfirmModal ref={confirmModalRef} />
        </BodyEnd>
    </>
}

export default PaymentRequest;